import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import UploadField from "./UploadField";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { title: "", brand: "", price: "", specs: "", tag: "", image: "" };
const inputCls = "w-full bg-[#0D1117] border border-[#263342] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-crimson transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2";

export default function VehiclesManager() {
  const [vehicles, setVehicles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vehicles");
      setVehicles(data);
    } catch (err) {
      toast.error("Could not load vehicles", { description: formatApiError(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ title: v.title, brand: v.brand, price: v.price, specs: v.specs || "", tag: v.tag || "", image: v.image || "" }); setDialogOpen(true); };
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/vehicles/${editing.id}`, form);
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", form);
        toast.success("Vehicle added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Vehicle removed");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  return (
    <div data-testid="vehicles-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100">Vehicles For Sale</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">Add, edit or remove stock</p>
        </div>
        <button
          data-testid="vehicle-add-button"
          onClick={openAdd}
          className="flex items-center gap-2 bg-crimson text-white font-display font-semibold uppercase tracking-widest text-sm px-5 py-2.5 hover:-translate-y-0.5 transition-transform"
        >
          <Plus size={16} /> Add Vehicle
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vehicles-manager-grid">
        {vehicles.map((v, i) => (
          <div key={v.id} className="bg-[#0D1117] border border-[#263342] overflow-hidden" data-testid={`vehicle-admin-card-${i}`}>
            {v.image && <img src={v.image} alt={v.title} className="w-full h-36 object-cover" loading="lazy" />}
            <div className="p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{v.brand}</p>
              <h3 className="font-display font-bold uppercase text-lg text-slate-100 leading-tight mt-1">{v.title}</h3>
              <p className="font-display font-bold text-xl text-crimson mt-2">{v.price}</p>
              <div className="mt-4 flex gap-2">
                <button
                  data-testid={`vehicle-edit-${i}`}
                  onClick={() => openEdit(v)}
                  className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-3 py-2 text-slate-300 hover:border-crimson hover:text-crimson transition-colors"
                >
                  <Pencil size={12} /> Edit
                </button>
                {confirmDelete === v.id ? (
                  <button
                    data-testid={`vehicle-delete-confirm-${i}`}
                    onClick={() => remove(v.id)}
                    className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-red-500 bg-red-500/10 px-3 py-2 text-red-400"
                  >
                    <Trash2 size={12} /> Confirm?
                  </button>
                ) : (
                  <button
                    data-testid={`vehicle-delete-${i}`}
                    onClick={() => setConfirmDelete(v.id)}
                    className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-3 py-2 text-slate-400 hover:border-red-500 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-panel border-[#263342] text-slate-100 max-w-xl" data-testid="vehicle-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-2xl">
              {editing ? "Edit Vehicle" : "Add Vehicle"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="mt-2 grid sm:grid-cols-2 gap-4" data-testid="vehicle-form">
            <div className="sm:col-span-2">
              <label className={labelCls}>Title *</label>
              <input required className={inputCls} data-testid="vehicle-form-title" value={form.title} onChange={set("title")} placeholder="2023 Porsche 911 GT3 (992)" />
            </div>
            <div>
              <label className={labelCls}>Brand *</label>
              <input required className={inputCls} data-testid="vehicle-form-brand" value={form.brand} onChange={set("brand")} placeholder="Porsche" />
            </div>
            <div>
              <label className={labelCls}>Price *</label>
              <input required className={inputCls} data-testid="vehicle-form-price" value={form.price} onChange={set("price")} placeholder="$98,500" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Specs</label>
              <input className={inputCls} data-testid="vehicle-form-specs" value={form.specs} onChange={set("specs")} placeholder="4.0L FLAT-6 | 502 HP | 4,200 MI" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Tag</label>
              <input className={inputCls} data-testid="vehicle-form-tag" value={form.tag} onChange={set("tag")} placeholder="Certified Pre-Owned" />
            </div>
            <div className="sm:col-span-2">
              <UploadField
                label="Photo"
                testid="vehicle-form-image"
                value={form.image}
                onChange={(url) => setForm((f) => ({ ...f, image: url }))}
              />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" data-testid="vehicle-form-save" disabled={saving} className="w-full bg-crimson text-white font-display font-semibold uppercase tracking-widest py-3.5 disabled:opacity-60">
                {saving ? "Saving..." : editing ? "Save Changes" : "Add Vehicle"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
